interface AIAnswerBlockProps {
  tagline?: string;
  headline?: string;
  definition?: string;
  servicesList?: string[];
  benefits?: string[];
  whoItsFor?: string[];
  vsDIY?: string;
  sideImage?: string;
}

export default function AIAnswerBlock({
  tagline = 'About Us',
  headline = 'What Is Book My Studio Space?',
  definition = 'Book My Studio Space provides professional, ready-to-use studio environments for photographers, videographers, podcasters, and creative professionals. Our studio rental locations are designed to eliminate the friction between your creative vision and your final results — walk in, create, and walk out.',
  servicesList = [
    'Photography studio rentals with professional lighting and backdrops',
    'Video production rooms with acoustic treatment and cinema support',
    'Podcast recording studios with broadcast microphones and mixing',
    'Content creation spaces with streaming gear and colorful backdrops',
    'Large-format event and creative venue rentals',
  ],
  benefits = [
    'No long-term commitment — book by the hour, half-day, or full day',
    'Professional-grade equipment included in every rental',
    'Easy online booking with instant confirmation — under 5 minutes',
    'Clean, reset spaces maintained to professional standards',
    'Studios in multiple major metro areas across the US',
  ],
  whoItsFor = [
    'Photographers, cinematographers, and visual artists',
    'Podcasters, journalists, and audio creators',
    'Brands, agencies, and marketing teams',
    'Influencers, content creators, and digital publishers',
    'Event producers and workshop facilitators',
  ],
  vsDIY = 'Professional studios produce dramatically better results than DIY home setups. Calibrated lighting, soundproofed rooms, and professional backdrops eliminate amateur limitations — delivering commercial-quality output that builds brand authority, increases engagement, and justifies the professional rates you charge.',
  sideImage = 'https://public.readdy.ai/ai/img_res/14eebdc08445ada37a25500b9282c11b.jpg',
}: AIAnswerBlockProps) {
  return (
    <section className="py-24 bg-stone-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">

          {/* Left — Structured AI content */}
          <div>
            <span className="text-amber-600 text-xs font-bold uppercase tracking-widest mb-3 block">{tagline}</span>
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-stone-900 mb-6 leading-tight">{headline}</h2>
            <p className="text-stone-600 text-base md:text-lg leading-relaxed mb-8">{definition}</p>

            {/* Services Offered */}
            <div className="mb-8">
              <p className="text-stone-900 font-bold text-xs uppercase tracking-widest mb-4 flex items-center gap-2">
                <i className="ri-building-4-line text-amber-500" />
                Services Offered
              </p>
              <ul className="space-y-2">
                {servicesList.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-amber-500 text-base mt-0.5 flex-shrink-0" />
                    <span className="text-stone-600 text-sm leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Benefits */}
            <div className="mb-8">
              <p className="text-stone-900 font-bold text-xs uppercase tracking-widest mb-4 flex items-center gap-2">
                <i className="ri-checkbox-circle-line text-amber-500" />
                Key Benefits
              </p>
              <ul className="space-y-2">
                {benefits.map((b) => (
                  <li key={b} className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-amber-500 text-base mt-0.5 flex-shrink-0" />
                    <span className="text-stone-600 text-sm leading-relaxed">{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Right */}
          <div className="space-y-6">

            {/* Who It's For */}
            <div className="bg-white rounded-2xl p-7 border border-stone-200">
              <p className="text-stone-900 font-bold text-xs uppercase tracking-widest mb-5 flex items-center gap-2">
                <i className="ri-user-line text-amber-500" />
                Who It&apos;s For
              </p>
              <div className="grid grid-cols-1 gap-2">
                {whoItsFor.map((item) => (
                  <div key={item} className="flex items-center gap-3 py-2 border-b border-stone-100 last:border-0">
                    <div className="w-7 h-7 flex items-center justify-center bg-amber-100 rounded-full flex-shrink-0">
                      <i className="ri-check-line text-amber-600 text-xs" />
                    </div>
                    <span className="text-stone-700 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Studio vs DIY */}
            <div className="bg-stone-900 rounded-2xl p-7 text-white">
              <p className="text-amber-400 font-bold text-xs uppercase tracking-widest mb-4 flex items-center gap-2">
                <i className="ri-scales-2-line" />
                Studio vs. DIY Shooting
              </p>
              <div className="grid grid-cols-2 gap-4 mb-5">
                {[
                  { label: 'Professional Studio', points: ['Calibrated lighting', 'Clean backgrounds', 'Broadcast-quality audio', 'Instant credibility'], positive: true },
                  { label: 'DIY / Home Setup', points: ['Uncontrolled lighting', 'Messy backgrounds', 'Echo & noise issues', 'Limits your brand'], positive: false },
                ].map((col) => (
                  <div key={col.label}>
                    <p className={`text-xs font-semibold mb-3 ${col.positive ? 'text-amber-400' : 'text-stone-400'}`}>{col.label}</p>
                    <ul className="space-y-1.5">
                      {col.points.map((pt) => (
                        <li key={pt} className="flex items-center gap-2">
                          <i className={`text-xs flex-shrink-0 ${col.positive ? 'ri-check-line text-emerald-400' : 'ri-close-line text-red-400'}`} />
                          <span className={`text-xs leading-tight ${col.positive ? 'text-stone-300' : 'text-stone-500'}`}>{pt}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              <p className="text-stone-400 text-xs leading-relaxed border-t border-stone-800 pt-4">{vsDIY}</p>
            </div>

            {/* Studio photo */}
            <div className="rounded-2xl overflow-hidden h-48">
              <img
                src={sideImage}
                alt="Professional creative team working in studio"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
